import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Code,
  Group,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconEye, IconSearch } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import type { ManagerClientListItem } from '@expeditor/shared';
import { managerClientsApi } from '../../shared/api/manager-clients';
import { useAuthStore } from '../../shared/auth/auth.store';

const TYPE_LABEL: Record<string, string> = {
  INDIVIDUAL: 'ФЛ',
  ENTREPRENEUR: 'ИП',
  LEGAL_RU: 'ЮЛ РФ',
  LEGAL_FOREIGN: 'Иностр.',
};

/**
 * Список всех клиентов для staff-ролей. Поиск по имени / ИНН / serviceId.
 */
export function AllClientsPage() {
  const user = useAuthStore((s) => s.user);
  const isManager = user?.role === 'MANAGER';
  const [items, setItems] = useState<ManagerClientListItem[]>([]);
  const [search, setSearch] = useState('');
  const [includeDeregistered, setIncludeDeregistered] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    try {
      const data = await managerClientsApi.list({
        search: search.trim() || undefined,
        includeDeregistered,
        mineOnly,
      });
      setItems(data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить');
    }
  };

  useEffect(() => {
    const t = setTimeout(reload, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, includeDeregistered, mineOnly]);

  return (
    <Stack>
      <Title order={2}>Все клиенты</Title>

      <Group>
        <TextInput
          leftSection={<IconSearch size={14} />}
          placeholder="Поиск по имени / ИНН / ID сервиса"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          w={400}
        />
        <Switch
          label="С прекращёнными"
          checked={includeDeregistered}
          onChange={(e) => setIncludeDeregistered(e.currentTarget.checked)}
        />
        {isManager && (
          <Switch
            label="Только мои"
            checked={mineOnly}
            onChange={(e) => setMineOnly(e.currentTarget.checked)}
          />
        )}
      </Group>

      {error && <Text c="red">{error}</Text>}

      <Table striped withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>ID сервиса</Table.Th>
            <Table.Th>Тип</Table.Th>
            <Table.Th>Наименование</Table.Th>
            <Table.Th>ИНН / Tax ID</Table.Th>
            <Table.Th>Контакты</Table.Th>
            <Table.Th>Ответственный</Table.Th>
            <Table.Th>Поручений</Table.Th>
            <Table.Th>Активных перевозок</Table.Th>
            <Table.Th>Статус</Table.Th>
            <Table.Th style={{ width: 60 }} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((c) => (
            <Table.Tr key={c.id}>
              <Table.Td>
                <Code>{c.serviceId}</Code>
              </Table.Td>
              <Table.Td>
                <Badge variant="light">{TYPE_LABEL[c.type] ?? c.type}</Badge>
              </Table.Td>
              <Table.Td>
                <Text component={Link} c="blue" to={`/manager/clients/${c.id}`} fw={500}>
                  {c.displayName}
                </Text>
              </Table.Td>
              <Table.Td>{c.primaryId ?? '—'}</Table.Td>
              <Table.Td>
                <Text size="xs">{c.phone ?? '—'}</Text>
                <Text size="xs" c="dimmed">
                  {c.email ?? '—'}
                </Text>
              </Table.Td>
              <Table.Td>
                {c.primaryManager ? (
                  <Text size="xs">
                    {c.primaryManager.fullName ?? c.primaryManager.email}
                  </Text>
                ) : (
                  <Badge color="gray" variant="light">
                    не закреплён
                  </Badge>
                )}
              </Table.Td>
              <Table.Td>{c.ordersTotal}</Table.Td>
              <Table.Td>
                {c.shipmentsActive > 0 ? (
                  <Badge color="orange">{c.shipmentsActive}</Badge>
                ) : (
                  '—'
                )}
              </Table.Td>
              <Table.Td>
                {c.deregisteredAt ? (
                  <Badge color="gray">прекращена</Badge>
                ) : c.isActive ? (
                  <Badge color="green">активен</Badge>
                ) : (
                  <Badge color="red">неактивен</Badge>
                )}
              </Table.Td>
              <Table.Td>
                <Tooltip label="Открыть карточку">
                  <ActionIcon component={Link} to={`/manager/clients/${c.id}`} variant="subtle">
                    <IconEye size={16} />
                  </ActionIcon>
                </Tooltip>
              </Table.Td>
            </Table.Tr>
          ))}
          {items.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={10}>
                <Text c="dimmed" ta="center" py="md">
                  Клиентов не найдено
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
